import { createTheme } from '@mui/material/styles'
import { BREAKPOINTS } from './breakpoints'

// Color palette
const colors = {
  // Header colors
  headerBg: '#0077C0',
  headerSelectBg: 'rgba(255, 255, 255, 0.15)',
  headerSelectBorder: 'rgba(255, 255, 255, 0.3)',
  headerSelectHoverBg: 'rgba(255, 255, 255, 0.2)',
  headerSelectText: '#ffffff',

  // FilterBar colors
  filterBarBg: '#005A94',
  filterBarSelectBg: 'rgba(0, 136, 212, 1)',
  filterBarSelectBorder: 'rgba(255, 255, 255, 0.3)',
  filterBarSelectHoverBg: 'rgba(0, 163, 224, 0.85)',
  filterBarSelectHoverBorder: 'rgba(255, 255, 255, 0.55)',
  filterBarSelectFocusBg: 'rgba(90, 148, 255, 0.2)',
  filterBarSelectFocusBorder: 'rgba(255, 255, 255, 0.6)',
  filterBarSelectText: '#ffffff',

  // Multi-select indicator colors
  indicatorCheck: '#00A3E0',
  indicatorBorder: '#00A3E0',
  indicatorHoverBg: 'rgba(0, 163, 224, 0.3)',
  indicatorSelectedBg: 'rgba(0, 163, 224, 0.2)',

  // Button / action colors
  primaryBtn: '#0077C0',
  primaryBtnHover: '#005A94',
  dangerBtn: '#c0392b',
  dangerBtnHover: '#a93226',
  ghostBtnText: '#444444',
  ghostBtnHover: 'rgba(0,0,0,0.06)',
}

export const muiTheme = createTheme({
  // Declared explicitly (these are MUI's defaults) so the theme can never drift
  // from src/theme/breakpoints.ts or from the literals in responsive.css.
  breakpoints: { values: BREAKPOINTS },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  components: {
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        },
      },
    },
    MuiMenuItem: {
      defaultProps: {
        dense: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            minHeight: 'var(--touch-target-min)',
          },
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            minHeight: 'var(--touch-target-min)',
          },
        }),
      },
    },
    MuiTooltip: {
      defaultProps: {
        // Touch has no hover, so an informational tooltip is unreachable without
        // these: tap shows it immediately and it stays long enough to read.
        enterTouchDelay: 0,
        leaveTouchDelay: 4000,
      },
    },
    MuiButton: {
      defaultProps: {
        size: 'small',
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: '4px',
        },
        containedPrimary: {
          backgroundColor: colors.primaryBtn,
          '&:hover': {
            backgroundColor: colors.primaryBtnHover,
          },
        },
        containedError: {
          backgroundColor: colors.dangerBtn,
          '&:hover': {
            backgroundColor: colors.dangerBtnHover,
          },
        },
        outlined: {
          borderColor: colors.primaryBtn,
          color: colors.primaryBtn,
          '&:hover': {
            backgroundColor: 'rgba(0,119,192,0.06)',
            borderColor: colors.primaryBtnHover,
          },
        },
        text: {
          color: colors.ghostBtnText,
          '&:hover': {
            backgroundColor: colors.ghostBtnHover,
          },
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        // Only force the ghost-button text color for the default color variant — otherwise
        // an explicit `color="primary"` (etc.) prop would be overridden by this root style.
        root: ({ ownerState, theme }) => ({
          borderRadius: '4px',
          ...(ownerState.color === 'default' && { color: colors.ghostBtnText }),
          '&:hover': {
            backgroundColor: colors.ghostBtnHover,
          },
          // Touch targets: 44px is the smallest reliably tappable square.
          [theme.breakpoints.down('sm')]: {
            minWidth: 'var(--touch-target-min)',
            minHeight: 'var(--touch-target-min)',
          },
        }),
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
        variant: 'outlined',
      },
    },
    // Every dialog becomes effectively full screen at compact, without touching call
    // sites. The popped-out notes window opts out by class: it computes its own
    // geometry and is docked below lg anyway. (FeedbackDialog is a Portal + Paper,
    // not a Dialog, so it is unaffected and handles compact itself.)
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            '&:not(.note-panel-window)': {
              margin: 0,
              width: '100%',
              maxWidth: '100%',
              height: '100%',
              maxHeight: '100%',
              borderRadius: 0,
            },
          },
        }),
      },
    },
    MuiCheckbox: {
      defaultProps: {
        size: 'small',
        disableRipple: false,
      },
      styleOverrides: {
        root: {
          color: colors.indicatorBorder,
          padding: '2px',
          '&.Mui-checked': {
            color: colors.indicatorCheck,
          },
          '&:hover': {
            backgroundColor: colors.indicatorHoverBg,
          },
        },
      },
    },
  },
})

// Header language select styling
export const headerSelectSx = {
  color: colors.headerSelectText,
  backgroundColor: colors.headerSelectBg,
  border: `1px solid ${colors.headerSelectBorder}`,
  borderRadius: '4px',
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiSvgIcon-root': {
    color: colors.headerSelectText,
  },
  '&:hover': {
    backgroundColor: colors.headerSelectHoverBg,
  },
}

// FilterBar select styling
export const filterBarSelectSx = {
	// Full width inside the compact filter sheet, fixed width in the wide bar.
	// The bar never renders these groups at xs, so 100% only ever applies in the sheet.
	width: { xs: '100%', sm: '200px' },
	color: colors.filterBarSelectText,
	backgroundColor: colors.filterBarSelectBg,
	border: `1px solid ${colors.filterBarSelectBorder}`,
	borderRadius: '4px',
	'& .MuiOutlinedInput-notchedOutline': {
		border: 'none',
	},
	'& .MuiSvgIcon-root': {
		color: colors.filterBarSelectText,
	},
	'&:hover': {
		backgroundColor: colors.filterBarSelectHoverBg,
		borderColor: colors.filterBarSelectHoverBorder,
	},
	'&.Mui-focused': {
		backgroundColor: colors.filterBarSelectFocusBg,
		borderColor: colors.filterBarSelectFocusBorder,
	},
}

// Multi-select menu item styling helpers
export const getMultiSelectMenuItemSx = (isChecked: boolean) => ({
  backgroundColor: isChecked ? colors.indicatorSelectedBg : 'transparent',
  '&:hover': {
    backgroundColor: colors.indicatorHoverBg,
  },
})

// Single-select menu item styling
export const singleSelectMenuItemSx = {
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}
